import React, { useCallback, useMemo, useState, useRef } from 'react';
import cn from 'classnames';
import Button from 'chameleon/ui-stack/ui/base/button';
import Icon from 'chameleon/ui-stack/ui/base/icon';
import RadioButtonWithLabel from 'chameleon/ui-stack/ui/basic/radio-button-with-label';
import { LoadingSpinner } from 'legacy-packages/ft-cb';

import {
  PREVIEW_NOW,
  PREVIEW_NOW_DESCRIPTION,
  PREVIEW_LATER,
  PREVIEW_LATER_DESCRIPTION,
  MAKE_COPY,
  DECLINE,
  DECLINE_DESCRIPTION,
  DEFAULT_DESCRIPTION_TEXT,
  ACCEPT_SHARE_LANDING_MODAL_DESCRIPTION,
} from '../constants';

const customActions = {
  [PREVIEW_NOW]: PREVIEW_NOW_DESCRIPTION,
  [PREVIEW_LATER]: PREVIEW_LATER_DESCRIPTION,
  [MAKE_COPY]: ACCEPT_SHARE_LANDING_MODAL_DESCRIPTION,
  [DECLINE]: DECLINE_DESCRIPTION,
};

const renderEditDescriptionAction = ({ children, onClick, disabled }) => (
  <Button
    isNaked
    disabled={disabled}
    className="text-blue-800 flex items-center"
    onClick={onClick}
  >
    {children}
  </Button>
);

const RightModalContent = (props) => {
  const {
    description,
    currentAction,
    setCurrentAction,
    assessmentCopyName,
    setAssessmentCopyName,
    isQuizSummary,
    isQuizOwner,
    handleUpdateDescription,
  } = props;

  const [editDescription, setEditDescription] = useState(false);
  const [isUpdatingDescription, setIsUpdatingDescription] = useState(false);
  const descriptionInput = useRef(null);

  const canEditDescription = isQuizSummary && isQuizOwner;

  const hasDescription = !!description;

  const handleAssessmentCopyNameChange = (event) => {
    setAssessmentCopyName(event.target.value);
  };

  const closeEditMode = useCallback((error = false) => {
    setIsUpdatingDescription(false);
    if (!error) {
      setEditDescription(false);
    }
  }, []);

  const renderActionButtons = useMemo(
    () =>
      Object.keys(customActions).map((actionLabel) => {
        const isCurrentAction = currentAction === actionLabel;
        return (
          <RadioButtonWithLabel
            checked={isCurrentAction}
            key={actionLabel}
            label={actionLabel}
            onClick={() => setCurrentAction(actionLabel)}
            classes={{
              label: 'text-gray-900 font-medium',
              icon: `${
                isCurrentAction ? 'text-gray-900' : 'text-gray-500'
              } stroke-current`,
            }}
          />
        );
      }),
    [currentAction],
  );

  const editDescriptionButton = renderEditDescriptionAction({
    children: (
      <React.Fragment>
        <Icon id="edit" width={14} height={14} />
        <span className="text-base font-normal leading-8 ml-0.8">
          Edit Description
        </span>
      </React.Fragment>
    ),
    onClick: () => setEditDescription(true),
  });

  const cancelEditButton = renderEditDescriptionAction({
    children: 'Cancel',
    onClick: () => closeEditMode(),
    disabled: isUpdatingDescription,
  });

  const saveEditButton = renderEditDescriptionAction({
    children: isUpdatingDescription ? (
      <LoadingSpinner containerClassName="flex" height={24} />
    ) : (
      'Save'
    ),
    onClick: () => {
      setIsUpdatingDescription(true);
      handleUpdateDescription(descriptionInput.current.value, closeEditMode);
    },
    disabled: isUpdatingDescription,
  });

  const renderEditDescriptionActions = () => {
    if (editDescription) {
      return (
        <div className="flex">
          {cancelEditButton}
          {saveEditButton}
        </div>
      );
    }
    return editDescriptionButton;
  };

  const descriptionTextClass =
    'font-content text-base text-gray-700 font-normal leading-8';

  return (
    <div>
      <div className="shared_assignment_description">
        <div className="flex justify-between items-center">
          <h3>Description</h3>
          {canEditDescription && renderEditDescriptionActions()}
        </div>
        <div
          className={cn(
            descriptionTextClass,
            'overflow-y-auto',
            'whitespace-pre-wrap',
            `${isQuizSummary ? 'height-280' : 'height-123'}`,
            {
              'border border-dashed border-black rounded p-2.5 bg-gray-100': !(
                hasDescription || editDescription
              ),
            },
          )}
        >
          {canEditDescription && editDescription ? (
            <textarea
              ref={descriptionInput}
              defaultValue={description}
              className={cn(
                descriptionTextClass,
                'w-full height-270 border border-gray-400 p-0.6 pr-4 resize-none',
              )}
            />
          ) : (
            description || DEFAULT_DESCRIPTION_TEXT
          )}
        </div>
      </div>
      {!isQuizSummary && (
        <div className="bg-blue-100 p-4 mt-4.6 text-gray-900">
          <div className="flex gap-x-8 mb-6">{renderActionButtons}</div>
          <div className="flex flex-col text-gray-800 font-normal text-14">
            <div className="font-bold">{currentAction}</div>
            {customActions[currentAction]}
            {currentAction === MAKE_COPY && (
              <input
                value={assessmentCopyName}
                onChange={handleAssessmentCopyNameChange}
                className="h-12.2 border border-gray-40 px-4.2 mt-2 text-gray-700"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RightModalContent;
